/**
 * GenerationProgress — SSE-driven progress display during prose generation.
 *
 * Opens EventSource to VITE_JOB_STATUS_URL?sub={sub}&jobId={jobId}
 * Displays stage labels from job-status Lambda.
 *
 * Phase 68 rewrite: removed approving stage + onOutlineReady callback;
 * added ordering stage and chapterProgress label rendering.
 */
import { useEffect, useState } from 'react';

interface ChapterProgress {
  current: number;
  total: number;
}

interface Stage {
  status: string;
  stage: string;
  label: string;
  chapterProgress?: ChapterProgress;
  chaptersDone?: number;
  chaptersTotal?: number;
  preservedLockedCount?: number;
  regeneratedCount?: number;
}

export interface GenerationSummary {
  regeneratedCount?: number;
  preservedLockedCount?: number;
}

const STAGE_ORDER = ['pending', 'ordering', 'generating', 'complete'];
const STALL_TIMEOUT_MS = 3 * 60 * 1000;

interface GenerationProgressProps {
  jobId: string;
  sub: string;
  onComplete: (summary?: GenerationSummary) => void;
  onError: (msg: string) => void;
}

export function GenerationProgress({ jobId, sub, onComplete, onError }: GenerationProgressProps) {
  const [currentStage, setCurrentStage] = useState<Stage>({
    status: 'pending',
    stage: 'pending',
    label: 'Queued...',
  });
  const [stageProgress, setStageProgress] = useState(0);
  const [stalled, setStalled] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (stalled) return;
    const url = `${import.meta.env.VITE_JOB_STATUS_URL}?sub=${encodeURIComponent(sub)}&jobId=${encodeURIComponent(jobId)}`;
    const es = new EventSource(url);

    let stallTimer = setTimeout(onStall, STALL_TIMEOUT_MS);
    function onStall() {
      es.close();
      setStalled('No progress updates for 3 minutes. Generation may still be running in the background.');
    }

    es.onmessage = (event) => {
      clearTimeout(stallTimer);
      stallTimer = setTimeout(onStall, STALL_TIMEOUT_MS);
      try {
        const data = JSON.parse(event.data) as Stage;

        // Render structured chapter counts in the label when generating (D-03)
        let displayLabel = data.label;
        const done = data.chaptersDone ?? data.chapterProgress?.current;
        const total = data.chaptersTotal ?? data.chapterProgress?.total;
        if (data.stage === 'generating' && typeof done === 'number' && typeof total === 'number') {
          displayLabel = `Generating chapter ${done} of ${total}...`;
        }
        setCurrentStage({ ...data, label: displayLabel });

        const idx = STAGE_ORDER.indexOf(data.stage);
        setStageProgress(idx >= 0 ? Math.round(((idx + 1) / STAGE_ORDER.length) * 100) : 0);

        if (data.status === 'complete') {
          clearTimeout(stallTimer);
          es.close();
          onComplete({
            regeneratedCount: data.regeneratedCount,
            preservedLockedCount: data.preservedLockedCount,
          });
        } else if (data.status === 'error') {
          clearTimeout(stallTimer);
          es.close();
          onError('Generation failed. Try again or check your sessions.');
        } else if (data.status === 'cancelled') {
          clearTimeout(stallTimer);
          es.close();
        }
      } catch {
        // Ignore parse errors
      }
    };

    es.onerror = () => {
      clearTimeout(stallTimer);
      es.close();
      setStalled('Connection lost while checking progress.');
    };

    return () => {
      clearTimeout(stallTimer);
      es.close();
    };
  }, [jobId, sub, onComplete, onError, stalled, attempt]);

  if (stalled) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 space-y-4">
        <p className="text-red-700 text-sm bg-red-50 border border-red-200 rounded p-3">{stalled}</p>
        <button
          type="button"
          onClick={() => { setStalled(null); setAttempt(a => a + 1); }}
          className="px-4 py-2 rounded-lg bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8 space-y-6">
      <div className="flex items-center gap-3">
        <svg className="animate-spin h-5 w-5 text-amber-500 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
        <p className="text-stone-700 font-medium">{currentStage.label}</p>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-stone-100 rounded-full h-2">
        <div
          className="bg-amber-500 h-2 rounded-full transition-all duration-500"
          style={{ width: `${stageProgress}%` }}
          role="progressbar"
          aria-valuenow={stageProgress}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      <p className="text-stone-400 text-xs">
        This takes 3–8 minutes. You can leave this page and come back.
      </p>
    </div>
  );
}
